import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PlusCircle, Zap, X } from 'lucide-react';
import { biz } from '../../lib/api';
import { StatusBadge, PriorityBadge } from './BusinessLayout';
import ImageUploader from '../../components/ImageUploader';

const initialForm = {
  customer_name: '', mobile: '', email: '', address: '',
  product: '', product_model: '', serial_number: '', invoice_number: '',
  purchase_date: '', warranty_status: 'in_warranty',
  category: '', sub_category: '', description: '', attachments: [],
  source: 'phone', priority: 'medium', location: '',
  assigned_to_name: '', expected_resolution_date: '', remarks: '',
};

export default function Complaints() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [filter, setFilter] = useState({ status: '', priority: '', q: '', breached_only: false });
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = { ...filter };
      Object.keys(q).forEach((k) => q[k] === '' || q[k] === false ? delete q[k] : null);
      const r = await biz.listComplaints(q);
      setRows(r);
    } catch (e) {
      toast.error('Failed to load complaints');
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (params.get('new') === '1') { setShowForm(true); params.delete('new'); setParams(params); }
  }, [params, setParams]);

  const save = async () => {
    if (!form.customer_name || !form.mobile || !form.category || !form.description) {
      toast.error('Customer, mobile, category and description are required'); return;
    }
    try {
      const c = await biz.createComplaint(form);
      toast.success(`Complaint ${c.complaint_number} registered`);
      setShowForm(false); setForm(initialForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  return (
    <div className="grid gap-3" data-testid="biz-complaints-root">
      <div className="flex items-center gap-2 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-black text-[#B71C1C]">Complaint Book</div>
          <h1 className="font-heading font-black text-2xl md:text-3xl">Customer Complaints</h1>
        </div>
        <button className="ml-auto btn-primary !bg-[#B71C1C] !border-[#B71C1C]"
          onClick={() => { setForm(initialForm); setShowForm(true); }}
          data-testid="biz-complaint-new-btn">
          <Zap className="w-4 h-4"/> New Complaint
        </button>
      </div>

      <div className="card-soft p-3 flex flex-wrap gap-2 items-end text-xs" data-testid="biz-complaints-filters">
        <F label="Status">
          <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
            <option value="">All</option>
            {['NEW', 'ACKNOWLEDGED', 'ASSIGNED', 'UNDER_INVESTIGATION', 'ACTION_REQUIRED', 'CUSTOMER_CONFIRMATION', 'RESOLVED', 'CLOSED', 'ON_HOLD', 'ESCALATED', 'REOPENED', 'CANCELLED'].map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
        </F>
        <F label="Priority">
          <select value={filter.priority} onChange={(e) => setFilter({ ...filter, priority: e.target.value })}>
            <option value="">All</option><option value="critical">Critical</option>
            <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
          </select>
        </F>
        <F label="Search"><input placeholder="Complaint #, customer, mobile, serial…" value={filter.q}
          onChange={(e) => setFilter({ ...filter, q: e.target.value })}/></F>
        <label className="inline-flex items-center gap-2 mt-4">
          <input type="checkbox" checked={filter.breached_only} onChange={(e) => setFilter({ ...filter, breached_only: e.target.checked })}
            data-testid="biz-filter-breached"/>
          SLA breached only
        </label>
      </div>

      <div className="card-soft overflow-x-auto" data-testid="biz-complaints-table">
        <table className="min-w-full text-sm">
          <thead className="bg-[#FFF8EE] text-[10px] uppercase tracking-widest text-ink/60">
            <tr>
              {['Complaint #', 'Customer', 'Product', 'Priority', 'Status', 'SLA', 'Assigned', 'Created'].map((h) =>
                <th key={h} className="text-left px-2 py-2 whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="8" className="p-4 text-ink/50">Loading…</td></tr>
              : !rows.length ? <tr><td colSpan="8" className="p-4 text-ink/50">No complaints found.</td></tr>
                : rows.map((r) => (
                  <tr key={r.complaint_number} className="border-t border-border/60 cursor-pointer hover:bg-[#FFF8EE]"
                    data-testid={`biz-complaint-row-${r.complaint_number}`}>
                    <td className="px-2 py-1.5"><Link to={`/business/complaints/${r.complaint_number}`} className="font-mono text-xs text-[#0D47A1]">{r.complaint_number}</Link></td>
                    <td className="px-2 py-1.5">
                      <div>{r.customer_name}</div>
                      <div className="text-[10px] text-ink/50">{r.mobile}</div>
                    </td>
                    <td className="px-2 py-1.5"><div>{r.product}</div><div className="text-[10px] text-ink/60 truncate max-w-xs">{r.description}</div></td>
                    <td className="px-2 py-1.5"><PriorityBadge value={r.priority}/></td>
                    <td className="px-2 py-1.5"><StatusBadge value={r.status}/></td>
                    <td className="px-2 py-1.5">
                      {r.sla_breached ? <span className="text-[10px] uppercase font-bold text-[#B71C1C]">BREACHED</span>
                        : <span className="text-[10px] text-ink/60">{Math.round(r.remaining_hours || 0)}h left</span>}
                    </td>
                    <td className="px-2 py-1.5 text-xs">{r.assigned_to_name || '—'}</td>
                    <td className="px-2 py-1.5 text-[11px] text-ink/60">{(r.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {showForm && <ComplaintForm form={form} setForm={setForm} onSave={save} onClose={() => setShowForm(false)}/>}
    </div>
  );
}

function F({ label, children }) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="uppercase tracking-widest text-[10px] text-ink/50">{label}</span>
      <span className="[&>*]:rounded-lg [&>*]:border [&>*]:border-border [&>*]:px-2 [&>*]:py-1.5 [&>*]:bg-white">{children}</span>
    </label>
  );
}

function L({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-widest text-ink/50">{label}</span>
      <span className="[&>*]:w-full [&>*]:rounded-lg [&>*]:border [&>*]:border-border [&>*]:px-2 [&>*]:py-1.5 [&>*]:bg-white">{children}</span>
    </label>
  );
}

function ComplaintForm({ form, setForm, onSave, onClose }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-2" data-testid="biz-complaint-modal" onClick={onClose}>
      <div className="w-full max-w-3xl bg-white rounded-2xl border border-border shadow-xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-heading font-bold">New Complaint</div>
          <button onClick={onClose} data-testid="biz-complaint-modal-close"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <L label="Customer name*"><input value={form.customer_name} onChange={(e) => set('customer_name', e.target.value)} data-testid="biz-cform-customer"/></L>
          <L label="Mobile*"><input value={form.mobile} onChange={(e) => set('mobile', e.target.value)} data-testid="biz-cform-mobile"/></L>
          <L label="Email"><input value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="biz-cform-email"/></L>
          <L label="Address / Location"><input value={form.address} onChange={(e) => set('address', e.target.value)} data-testid="biz-cform-address"/></L>

          <L label="Product"><input value={form.product} onChange={(e) => set('product', e.target.value)} data-testid="biz-cform-product"/></L>
          <L label="Model"><input value={form.product_model} onChange={(e) => set('product_model', e.target.value)}/></L>
          <L label="Serial #"><input value={form.serial_number} onChange={(e) => set('serial_number', e.target.value)}/></L>
          <L label="Invoice #"><input value={form.invoice_number} onChange={(e) => set('invoice_number', e.target.value)}/></L>

          <L label="Purchase date"><input type="date" value={form.purchase_date} onChange={(e) => set('purchase_date', e.target.value)}/></L>
          <L label="Warranty">
            <select value={form.warranty_status} onChange={(e) => set('warranty_status', e.target.value)}>
              <option value="in_warranty">In warranty</option>
              <option value="out_of_warranty">Out of warranty</option>
              <option value="extended">Extended</option>
              <option value="unknown">Unknown</option>
            </select>
          </L>
          <L label="Category*"><input value={form.category} onChange={(e) => set('category', e.target.value)} data-testid="biz-cform-category"/></L>
          <L label="Sub-category"><input value={form.sub_category} onChange={(e) => set('sub_category', e.target.value)}/></L>

          <L label="Source">
            <select value={form.source} onChange={(e) => set('source', e.target.value)} data-testid="biz-cform-source">
              {['phone', 'whatsapp', 'email', 'website', 'walkin', 'sales', 'service', 'dealer', 'other'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </L>
          <L label="Priority">
            <select value={form.priority} onChange={(e) => set('priority', e.target.value)} data-testid="biz-cform-priority">
              <option value="critical">Critical</option><option value="high">High</option>
              <option value="medium">Medium</option><option value="low">Low</option>
            </select>
          </L>
          <L label="Assigned to"><input value={form.assigned_to_name} onChange={(e) => set('assigned_to_name', e.target.value)}/></L>
          <L label="Expected resolution date"><input type="date" value={form.expected_resolution_date} onChange={(e) => set('expected_resolution_date', e.target.value)}/></L>

          <div className="col-span-full"><L label="Description*"><textarea rows="2" value={form.description} onChange={(e) => set('description', e.target.value)} data-testid="biz-cform-description"/></L></div>
          <div className="col-span-full">
            <div className="text-[11px] font-bold text-ink/70 mb-1">Photos of the problem (up to 4)</div>
            <div className="flex flex-wrap gap-2 items-start">
              {(form.attachments || []).map((a, i) => (
                <div key={a.url} className="relative" data-testid={`biz-cform-photo-${i}`}>
                  <img src={a.url} alt={`Complaint ${i + 1}`} className="w-20 h-20 rounded-lg object-cover border border-border"/>
                  <button type="button" data-testid={`biz-cform-photo-remove-${i}`} onClick={() => set('attachments', form.attachments.filter((_, j) => j !== i))}
                    className="absolute -top-2 -right-2 bg-white rounded-full border border-border w-5 h-5 grid place-items-center" aria-label="Remove photo"><X className="w-3 h-3"/></button>
                </div>
              ))}
              {(form.attachments || []).length < 4 && (
                <ImageUploader testId="biz-cform-photo-uploader" label="complaint photo" value=""
                  onChange={(url) => url && set('attachments', [...(form.attachments || []), { url, type: 'image', name: `photo-${Date.now()}` }])}/>
              )}
            </div>
          </div>
          <div className="col-span-full"><L label="Remarks"><textarea rows="1" value={form.remarks} onChange={(e) => set('remarks', e.target.value)}/></L></div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button className="pill !bg-white" onClick={onClose}>Cancel</button>
          <button className="btn-primary !bg-[#B71C1C] !border-[#B71C1C]" onClick={onSave} data-testid="biz-cform-save">Register complaint</button>
        </div>
      </div>
    </div>
  );
}
